"use client";

import { forwardRef } from "react";
import BootstrapBtn from "react-bootstrap/Button";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useButtonEventTracking } from "@hooks/use-button-event-tracking";
import CustomLoader from "../CustomLoader";
import IconSvg from "../IconSvg";

/**
 * variant: "primary" | "secondary" | "tertiary" | "dark" |
 * "middle-primary" | "middle-secondary" | "middle-tertiary" | "middle-dark"
 * "outline-primary" | "outline-secondary" | "outline-tertiary" | "outline-dark"
 * "link-primary" | "link-secondary" | "link-tertiary" | "link-dark"
 * "ghost-primary" | "ghost-secondary" | "ghost-tertiary" | "ghost-dark"

 * size: "xs" | "sm" | "lg" (or nothing for default md size)
 * square: true | false => force a square shape
 * pill: true | false => force a pill shape and smaller horizontal padding
 * hugContent: true | false
 * spaceBetween: true | false => space between left and right icons
 * leftIconName: string => name of the icon to display on the left side
 * rightIconName: string => name of the icon to display on the right side
 * loading: boolean => if true, shows a loader inside the button
 * disabled: boolean => if true, disables the button
 * fullWidth: boolean => if true, makes the button take the full width of its container
 */

const Button = forwardRef(function Button(
  {
    trackingName = "",
    title,
    size,
    children,
    loading = false,
    disabled,
    fullWidth = false,
    hugContent = false,
    spaceBetween,
    pill = false,
    square = false,
    className,
    leftIconName,
    rightIconName,
    onClick = () => {},
    userGroup,
    eventAdditionalData,
    ...other
  },
  ref,
) {
  // eslint-disable-next-line no-unused-vars
  const { tReady, ...otherProps } = other;

  const trackEvent = useButtonEventTracking();

  function handleClick(event) {
    const buttonName = title || children || leftIconName;

    trackEvent(trackingName, buttonName, userGroup, eventAdditionalData);
    onClick(event);
  }

  return (
    <BootstrapBtn
      {...otherProps}
      ref={ref}
      size={size}
      className={classNames(
        loading && "loading",
        disabled && "disabled",
        fullWidth && "w-100",
        hugContent && "hug-content",
        pill && "pill",
        ((!children && !fullWidth) || square) && "square",
        className,
      )}
      disabled={disabled || loading}
      title={title || children}
      onClick={handleClick}
    >
      <div
        className={classNames(
          "btn-container",
          spaceBetween && "justify-content-between",
          !children && "icon-only",
        )}
      >
        {leftIconName && (
          <div className={classNames("icon-container", size)}>
            <IconSvg name={leftIconName} />
          </div>
        )}

        {children && loading ? (
          <span
            className="btn-loader-container"
            title={children}
          >
            <CustomLoader
              variant="SpinnerMiddleGray"
              className={classNames("btn-loader", size)}
            />
          </span>
        ) : (
          children && <span className="btn-children-container">{children}</span>
        )}

        {rightIconName && (
          <div className={classNames("icon-container", size)}>
            <IconSvg name={rightIconName} />
          </div>
        )}
      </div>
    </BootstrapBtn>
  );
});

Button.propTypes = {
  loading: PropTypes.bool,
  fullWidth: PropTypes.bool,
  hugContent: PropTypes.bool,
  square: PropTypes.bool,
  onClick: PropTypes.func,
  trackingName: PropTypes.string,
};

export default Button;
